import { get, writable } from 'svelte/store';
import type { MenuListDataType } from '../../components/Profiles/DropdownItem.types';
import {
	mapProfilesForNative,
	notifyNativeProfiles,
} from 'src/helpers/native_bridge_helpers';
import { user_data } from '../account/user';
import { normalizeEmail } from 'src/helpers/email_helpers';
import { MAX_LOGGED_IN_PROFILES } from 'src/constants';

export const showProfileModal = writable<boolean>(false);

export const getProfileAvatarText = (title?: string, email?: string): string => {
	return (title || email || '').slice(0, 1).toUpperCase();
};

function createMultipleProfilesStore() {
	const { subscribe, set, update } = writable<MenuListDataType[]>(
		JSON.parse(localStorage.getItem('profiles')) || [],
	);

	/**
	 * Validation of avatar object with correct types.
	 */
	const cleanAvatar = (avatar: MenuListDataType['acc_avatar'] | undefined) => {
		if (!avatar) return {};

		const cleanedAvatar = { ...avatar };

		if (cleanedAvatar?.image && typeof cleanedAvatar.image !== 'string') {
			delete cleanedAvatar.image;
		}

		if (cleanedAvatar?.backgroundColor && typeof cleanedAvatar.backgroundColor !== 'string') {
			delete cleanedAvatar.backgroundColor;
		}

		return cleanedAvatar;
	};

	return {
		subscribe,
		set,
		update,
		add: (newProfile: MenuListDataType) =>
			update((profiles) => {
				const sanitizedProfile = {
					...newProfile,
					acc_avatar: {
						...cleanAvatar(newProfile.acc_avatar),
						text: getProfileAvatarText(newProfile.title, newProfile.email),
					},
				};

				const existingIndex = profiles.findIndex(
					(item) => normalizeEmail(item.email) === normalizeEmail(newProfile.email),
				);

				if (existingIndex !== -1) {
					return profiles.map((item, i) => {
						if (i === existingIndex) {
							return {
								...item,
								...sanitizedProfile,
								isSelected: true,
							};
						}
						return {
							...item,
							isSelected: false,
						};
					});
				}

				if (profiles.length >= MAX_LOGGED_IN_PROFILES) {
					console.warn(
						'[profiles] Refusing to add a profile beyond the logged-in account limit',
					);
					return profiles;
				}

				const updatedProfiles = profiles.map((item) => {
					return {
						...item,
						isSelected: false,
					};
				});

				return [sanitizedProfile, ...updatedProfiles];
			}),
		change: (email: string) => {
			update((profiles) => {
				const updatedProfiles = profiles
					.map((item) => {
						if (email === item.email) {
							return {
								...item,
								isSelected: true,
							};
						}
						return {
							...item,
							isSelected: false,
						};
					})
					.sort((a, b) => Number(b.isSelected) - Number(a.isSelected));
				return [...updatedProfiles];
			});
		},
		edit: (editedProfile: MenuListDataType) => {
			update((profiles) => {
				const updatedProfiles = profiles.map((item) => {
					if (editedProfile.email === item.email) {
						return {
							...item,
							...editedProfile,
							acc_avatar: {
								...cleanAvatar(editedProfile.acc_avatar),
								text: getProfileAvatarText(editedProfile.title, editedProfile.email),
							},
						};
					}
					return item;
				});
				return [...updatedProfiles];
			});
		},
		remove: (email: string) => {
			update((profiles) => {
				const normalized = normalizeEmail(email);
				const removedWasSelected = profiles.some(
					(item) => normalizeEmail(item.email) === normalized && item.isSelected,
				);
				const filteredProfiles = profiles.filter(
					(item) => normalizeEmail(item.email) !== normalized,
				);

				if (!removedWasSelected) {
					return [...filteredProfiles];
				}

				return filteredProfiles.map((item, i) => ({
					...item,
					isSelected: i === 0,
				}));
			});
		},
		reset: () => {
			return [];
		},
	};
}

export const profiles = createMultipleProfilesStore();

const notifyNativeProfilesFromStores = () => {
	const session = get(user_data);
	notifyNativeProfiles(
		mapProfilesForNative(get(profiles), {
			email: session.email,
			installer: session.installer,
		}),
	);
};

profiles.subscribe((val) => {
	localStorage.setItem('profiles', JSON.stringify(val));
	notifyNativeProfilesFromStores();
});
